import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Id } from '@cvx/_generated/dataModel'
import { Button } from '@rekrytering/ui/components/button'
import { Input } from '@rekrytering/ui/components/input'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'

export const RejectReasonsSettingsPage = () => {
  const [newLabel, setNewLabel] = useState('')
  const [editingId, setEditingId] = useState<Id<'rejectReasons'> | undefined>(
    undefined,
  )
  const [editLabel, setEditLabel] = useState('')

  const { data: reasons = [] } = useQuery(
    convexQuery(api.features.candidates.reject_reasons.listMyRejectReasons, {}),
  )

  const { mutateAsync: createReason } = useMutation({
    mutationFn: useConvexMutation(
      api.features.candidates.reject_reasons.createMyRejectReason,
    ),
  })

  const { mutateAsync: updateReason } = useMutation({
    mutationFn: useConvexMutation(
      api.features.candidates.reject_reasons.updateMyRejectReason,
    ),
  })

  const { mutateAsync: deleteReason } = useMutation({
    mutationFn: useConvexMutation(
      api.features.candidates.reject_reasons.deleteMyRejectReason,
    ),
  })

  const handleCreate = async (): Promise<void> => {
    const trimmed = newLabel.trim()
    if (!trimmed) return
    await createReason({ label: trimmed })
    setNewLabel('')
  }

  const handleStartEdit = (id: Id<'rejectReasons'>, label: string): void => {
    setEditingId(id)
    setEditLabel(label)
  }

  const handleSaveEdit = async (): Promise<void> => {
    if (!editingId) return
    const trimmed = editLabel.trim()
    if (!trimmed) return
    await updateReason({ rejectReasonId: editingId, label: trimmed })
    setEditingId(undefined)
    setEditLabel('')
  }

  const handleDelete = async (id: Id<'rejectReasons'>): Promise<void> => {
    await deleteReason({ rejectReasonId: id })
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <div>
        <h2 className="text-base font-semibold text-primary">Reject reasons</h2>
        <p className="mt-1 text-sm text-primary/60">
          Define structured reasons that recruiters select when rejecting
          candidates.
        </p>
      </div>

      <div className="flex gap-2">
        <Input
          className="w-72"
          placeholder="New reject reason..."
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void handleCreate()
          }}
        />
        <Button size="sm" onClick={() => void handleCreate()}>
          Add
        </Button>
      </div>

      {reasons.length === 0 ? (
        <p className="text-sm text-primary/40">
          No reject reasons defined yet.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {reasons.map((reason) => (
            <li
              key={reason._id}
              className="flex items-center justify-between gap-4 rounded-lg border border-border px-4 py-3"
            >
              {editingId === reason._id ? (
                <div className="flex flex-1 gap-2">
                  <Input
                    className="flex-1"
                    value={editLabel}
                    onChange={(e) => setEditLabel(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void handleSaveEdit()
                      if (e.key === 'Escape') setEditingId(undefined)
                    }}
                    autoFocus
                  />
                  <Button size="sm" onClick={() => void handleSaveEdit()}>
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditingId(undefined)}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <>
                  <span className="text-sm text-primary">{reason.label}</span>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleStartEdit(reason._id, reason.label)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void handleDelete(reason._id)}
                    >
                      Delete
                    </Button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
